<script setup lang="ts">
import { CheckCircleIcon, XCircleIcon } from '@gohighlevel/ghl-icons/24/outline'
import CustomModal from '@/components/common/CustomModal.vue'
import { computed, ref, watch } from 'vue'
import {
  DefaultValidationObjectForDocument,
  ModalEvents,
} from '@gohighlevel/ghl-proposals-common'
import { useProductListStore } from '@/store/productListStore'
import { useAppStore } from '@/store/app'
import { useBuilderStore } from '@/store/builder'

const productListStore = useProductListStore()
const appStore = useAppStore()
const builderStore = useBuilderStore()

const props = defineProps({
  loading: { type: Boolean, default: false },
  show: { type: Boolean, default: false },
})
defineEmits([ModalEvents.ON_SUCCESS, ModalEvents.ON_CANCEL])
const showErrorModal = ref(false)
const showLoading = ref(false)
const validations = ref(DefaultValidationObjectForDocument)
const isAllValid = ref(false)
watch(props, updatedProps => {
  showErrorModal.value = updatedProps.show
  showLoading.value = updatedProps.loading
})

const redirectUrlErrors = computed(() => appStore.getRedirectUrlSettingErrors())
const scheduleErrors = computed(() => productListStore.getProductListErrors())
const settingsErrors = ref(appStore.getSettingsErrors())
const customValueLinkageErrors = computed(() =>
  builderStore.getCustomValueLinkageErrors()
)

watch(
  () => appStore.document.notificationSettings,
  () => {
    settingsErrors.value = appStore.getSettingsErrors()
  },
  { deep: true }
)
</script>

<template>
  <CustomModal
    :close-on-success="false"
    :loading="showLoading"
    :disabled="showLoading"
    :show="showErrorModal"
    :title="
      isAllValid ? $t('common.validDocument') : $t('common.invalidDocument')
    "
    :description="!isAllValid ? $t('validations.invalid.docMustFields') : ''"
    @on-cancel="$emit(ModalEvents.ON_CANCEL)"
    @on-success="$emit(ModalEvents.ON_SUCCESS)"
    :cancelActionText="isAllValid ? $t('common.cancel') : $t('common.ok')"
    :successActionText="isAllValid ? $t('common.send') : ''"
    :modalType="isAllValid ? 'success' : 'error'"
  >
    <div class="flex flex-col" v-if="!isAllValid">
      <div class="flex items-center mb-3">
        <span
          :class="`h-4 w-4 mr-2 ${
            validations.isRecipientsAdded ? 'text-success-500' : 'text-red-500'
          }`"
          ><CheckCircleIcon v-if="validations.isRecipientsAdded" />
          <XCircleIcon v-if="!validations.isRecipientsAdded"
        /></span>
        {{
          validations.isRecipientsAdded
            ? $t('validations.invalid.recipientsSelected')
            : $t('validations.invalid.missingPrimaryRecipient')
        }}
      </div>
      <div class="flex items-center">
        <span
          :class="`h-4 w-4 mr-2 ${
            !appStore.document.notificationSettings ||
            validations.isNotificationSettingsValid
              ? 'text-success-500'
              : 'text-red-500'
          }`"
          ><CheckCircleIcon
            v-if="
              !appStore.document.notificationSettings ||
              validations.isNotificationSettingsValid
            "
          />
          <XCircleIcon
            v-if="
              appStore.document.notificationSettings &&
              !validations.isNotificationSettingsValid
            "
          />
        </span>
        {{
          !appStore.document.notificationSettings ||
          validations.isNotificationSettingsValid
            ? $t('common.validEmailConfiguration')
            : $t('common.invalidEmailConfiguration')
        }}
      </div>
      <div class="flex items-center">
        <span>
          <ul style="list-style-type: disc">
            <li v-for="(settingsError, index) in settingsErrors" :key="index">
              {{ settingsError }}
            </li>
          </ul>
        </span>
      </div>

      <div class="flex items-center">
        <span
          :class="`h-4 w-4 mr-2 ${
            validations.isRedirectUrlSettingsValid
              ? 'text-success-500'
              : 'text-red-500'
          }`"
          ><CheckCircleIcon v-if="validations.isRedirectUrlSettingsValid" />
          <XCircleIcon v-if="!validations.isRedirectUrlSettingsValid" />
        </span>
        {{
          validations.isRedirectUrlSettingsValid
            ? $t('validations.invalid.validRedirectUrlConfig')
            : $t('validations.invalid.invalidRedirectUrlConfig')
        }}
      </div>
      <div class="flex items-center">
        <span>
          <ul style="list-style-type: disc">
            <li
              v-for="(redirectUrlError, index) in redirectUrlErrors"
              :key="index"
            >
              {{ redirectUrlError }}
            </li>
          </ul>
        </span>
      </div>

      <div class="flex items-center">
        <span
          :class="`h-4 w-4 mr-2 ${
            validations.isProductListValid ? 'text-success-500' : 'text-red-500'
          }`"
          ><CheckCircleIcon v-if="validations.isProductListValid" />
          <XCircleIcon v-if="!validations.isProductListValid" />
        </span>
        {{
          validations.isProductListValid
            ? $t('validations.invalid.validProductListConfig')
            : $t('validations.invalid.productListConfig')
        }}
      </div>
      <div class="flex items-center">
        <span>
          <ul style="list-style-type: disc">
            <li v-for="(scheduleError, index) in scheduleErrors" :key="index">
              {{ scheduleError }}
            </li>
          </ul>
        </span>
      </div>

      <div class="flex items-center">
        <span
          :class="`h-4 w-4 mr-2 ${
            validations.isCustomValueLinkageValid
              ? 'text-success-500'
              : 'text-red-500'
          }`"
          ><CheckCircleIcon v-if="validations.isCustomValueLinkageValid" />
          <XCircleIcon v-if="!validations.isCustomValueLinkageValid" />
        </span>
        {{
          validations.isCustomValueLinkageValid
            ? $t('validations.invalid.validCustomValueLinkageConfig')
            : $t('validations.invalid.invalidCustomValueLinkageConfig')
        }}
      </div>
      <div class="flex items-center">
        <span>
          <ul style="list-style-type: disc">
            <li
              v-for="(
                customValueLinkageError, index
              ) in customValueLinkageErrors"
              :key="index"
            >
              {{ customValueLinkageError }}
            </li>
          </ul>
        </span>
      </div>

      <div class="flex items-center mb-3">
        <span
          :class="`h-4 w-4 mr-2 ${
            validations.isImageSizeValid ? 'text-success-500' : 'text-red-500'
          }`"
        >
          <CheckCircleIcon v-if="validations.isImageSizeValid" />
          <XCircleIcon v-if="!validations.isImageSizeValid" />
        </span>
        {{
          validations.isImageSizeValid
            ? $t('validations.invalid.validImageSizeConfig')
            : $t('validations.invalid.invalidImageSizeConfig')
        }}
      </div>

      <!-- Document title validation -->
      <div class="flex items-start">
        <span
          :class="`h-4 w-4 mr-2 mt-1 ${
            validations.isDocumentNameValid
              ? 'text-success-500'
              : 'text-red-500'
          }`"
        >
          <CheckCircleIcon v-if="validations.isDocumentNameValid" />
          <XCircleIcon v-if="!validations.isDocumentNameValid" />
        </span>
        {{
          validations.isDocumentNameValid
            ? $t('validations.invalid.documentTitleValid')
            : $t('validations.invalid.documentTitleInvalid')
        }}
      </div>
    </div>
  </CustomModal>
</template>
